import { makeAutoObservable, observable, runInAction } from "mobx";
import { StoreResults } from "../../../../stores/stores-helper";
import { deleteLinerMaterial, editLinerMaterial, getAllLinerMaterials, postNewLinerMaterial } from "features/provisioning/liner-material-library/services/liner-materal-service";
import { materialTypesData } from "features/provisioning/liner-material-library/utils/liner-material-type-data";
import { LinerSpecification } from "../models/liner-material-library-models";


class LinerMaterialStore {
  linerMaterialMap: Record<string, LinerSpecification> = observable.object({});
  linerMaterialLoading: boolean = false;


  constructor() {
    makeAutoObservable(this);
  }

  get linerMaterials() {
    return Object.values(this.linerMaterialMap);
  }

  get materialTypes() {
    return materialTypesData
  }

  loadAllLinerMaterials = async () => {
    if (this.linerMaterialLoading) {
      return;
    }
    runInAction(() => {
      this.linerMaterialLoading = true;
    });

    const data = await getAllLinerMaterials();
    try {
      runInAction(() => {
        for (let linerMaterial of data) {
          this.linerMaterialMap[linerMaterial.id] = observable.object(linerMaterial);
        }
        this.sortLinerMaterialMap();
      });
    } catch (e) {
      console.log(e);
    }
    finally {
      this.linerMaterialLoading = false;
    }
  };

  private sortLinerMaterialMap() {
    const sortedKeys = Object.keys(this.linerMaterialMap).sort((a, b) => {
      return a < b ? -1 : 1
    });

    const sortedLinerMaterialMap: Record<string, LinerSpecification> = {};
    for (const key of sortedKeys) {
      sortedLinerMaterialMap[key] = this.linerMaterialMap[key];
    }

    this.linerMaterialMap = sortedLinerMaterialMap;
  }

  createEntity = async (
    entity: LinerSpecification,
  ): Promise<StoreResults> => {
    if (this.linerMaterialLoading) {
      return { passed: false, message: "", entityId: "" };
    }

    runInAction(() => {
      this.linerMaterialLoading = true;
    });

    try {
      const response = await postNewLinerMaterial(entity);
      if (response.statusCode === 200) {
        const createdEntity = response.result;
        const entityId = createdEntity?.id ?? "";

        runInAction(() => {
          this.linerMaterialMap[entityId] = observable.object(createdEntity as LinerSpecification);
          this.sortLinerMaterialMap();
        });
        return { passed: true, message: "", entityId };
      }
      else if (response.statusCode === 409) {
        return { passed: false, message: "Conflict-409: an entity with the same name is exist.", entityId: "" };
      }
      else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message, entityId: "" };
      }
    }
    catch (error) {
      console.error("Error creating entity:", error);
      return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
    } finally {
      runInAction(() => {
        this.linerMaterialLoading = false;
      });
    }
  };

  editEntity = async (
    entity: LinerSpecification,
  ): Promise<StoreResults> => {

    if (this.linerMaterialLoading) {
      return { passed: false, message: "", entityId: "" };
    }

    runInAction(() => {
      this.linerMaterialLoading = true;
    });
    try {
      const response = await editLinerMaterial(entity);

      if (response.statusCode === 200) {
        this.linerMaterialMap[entity.id] = observable.object(entity as LinerSpecification);
        return { passed: true, message: "", entityId: entity.id };
      } else if (response.statusCode === 409) {
        return { passed: false, message: "Conflict-409: an entity with the same name is exist.", entityId: "" };
      }
      else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message, entityId: "" };
      }
    } catch (error) {
      console.error("Error editing entity:", error);
      return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
    } finally {
      runInAction(() => {
        this.linerMaterialLoading = false;
      });
    }
  }

  deleteEntity = async (
    entityId: string,
  ): Promise<StoreResults> => {
    if (this.linerMaterialLoading) {
      return { passed: false, message: "" };
    }

    runInAction(() => {
      this.linerMaterialLoading = true;
    });
    try {
      const response = await deleteLinerMaterial(entityId as string);
      if (response.statusCode === 200) {
        delete this.linerMaterialMap[entityId];
        return { passed: true, message: "" };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message };
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      return { passed: false, message: "An error has occurred, please check the logs" };
    } finally {
      runInAction(() => {
        this.linerMaterialLoading = false;
      });
    }
  }
}
export default new LinerMaterialStore();